import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Checkbox,
  DataTable,
  Select,
  ToggleSwitch,
  Tooltip,
  type BadgeColor,
  type DataTableColumn,
} from '@enamelai/design-system'
import { Icon as InfoIcon } from '@enamelai/design-system/icons/Info'
import { useTheme } from '@context/ThemeContext'
import { buildTimeOptionsAmPm } from '@utils/phoneManagement.utils'
import SettingsRowActions from '../../shared/SettingsRowActions.component'
import {
  sortRows,
  usePagedRows,
  useTableSort,
} from '../../shared/settingsTable'
import {
  DateOverrideRow,
  HolidayRow,
  holidayApiToForm,
  OPENING_HOURS_DAYS,
  overrideApiToForm,
} from './openingHours.data'
import useOpeningHours from './useOpeningHours'
import AddOverride from './AddOverride/AddOverride.component'
import AddHoliday from './AddHoliday/AddHoliday.component'
import { DeleteConfirmationModal } from '@components/layout/Modals'

import './OpeningHoursStyle.css'

const TIME_OPTIONS = buildTimeOptionsAmPm(30).map((time) => ({
  value: time,
  label: time,
}))
const DAY_OPTIONS = OPENING_HOURS_DAYS.map((day) => ({
  value: day,
  label: day,
}))
/** Grid dates are MM/DD/YYYY; compare them as YYYYMMDD. */
const toSortableDate = (value: string): string => {
  const [m, d, y] = value.split('/')
  return y && m && d ? `${y}${m}${d}` : value
}

const readSortValue = <T extends Record<string, unknown>>(
  row: T,
  columnId: string,
): string => {
  const raw = String(row[columnId] ?? '')
  return columnId === 'date' ? toSortableDate(raw) : raw.toLowerCase()
}

type SectionHeaderProps = {
  title: string
  info: string
  action?: React.ReactNode
}

function SectionHeader({
  title,
  info,
  action,
}: SectionHeaderProps): React.ReactElement {
  return (
    <div className="oh-section-header">
      <div className="oh-section-heading">
        <h2 className="oh-section-title">{title}</h2>
        <Tooltip content={info}>
          <span className="oh-info-icon" aria-label={info}>
            <InfoIcon aria-hidden="true" />
          </span>
        </Tooltip>
      </div>
      {action}
    </div>
  )
}

function OpeningHours(): React.ReactElement {
  const hooks = useOpeningHours()
  const {
    handleDeleteHoliday,
    handleDeleteOverride,
    handleReset,
    handleSave,
    hasChanges,
    setHeaderButtons,
  } = hooks
  const { resolvedTheme } = useTheme()
  const badgeMode = resolvedTheme === 'dark' ? 'solid' : 'tinted'
  const disabled = hooks.loading || !hooks.clinicId
  const [addOverrideOpen, setAddOverrideOpen] = useState(false)
  const [editingOverrideId, setEditingOverrideId] = useState<
    string | undefined
  >()
  const [addHolidayOpen, setAddHolidayOpen] = useState(false)
  const [editingHolidayId, setEditingHolidayId] = useState<string | undefined>()
  const [overrideToDelete, setOverrideToDelete] =
    useState<DateOverrideRow | null>(null)
  const [holidayToDelete, setHolidayToDelete] = useState<HolidayRow | null>(
    null,
  )
  const [overrideSort, toggleOverrideSort] = useTableSort()
  const [holidaySort, toggleHolidaySort] = useTableSort()

  const openAddOverride = useCallback((): void => {
    setEditingOverrideId(undefined)
    setAddOverrideOpen(true)
  }, [])

  const openEditOverride = useCallback((id: string): void => {
    setEditingOverrideId(id)
    setAddOverrideOpen(true)
  }, [])

  const closeAddOverride = useCallback((): void => {
    setAddOverrideOpen(false)
    setEditingOverrideId(undefined)
  }, [])

  const openAddHoliday = useCallback((): void => {
    setEditingHolidayId(undefined)
    setAddHolidayOpen(true)
  }, [])

  const openEditHoliday = useCallback((id: string): void => {
    setEditingHolidayId(id)
    setAddHolidayOpen(true)
  }, [])

  const closeAddHoliday = useCallback((): void => {
    setAddHolidayOpen(false)
    setEditingHolidayId(undefined)
  }, [])

  const confirmDeleteOverride = useCallback((): void => {
    if (!overrideToDelete) return
    void handleDeleteOverride(overrideToDelete.id)
    setOverrideToDelete(null)
  }, [overrideToDelete, handleDeleteOverride])

  const confirmDeleteHoliday = useCallback((): void => {
    if (!holidayToDelete) return
    void handleDeleteHoliday(holidayToDelete.id)
    setHolidayToDelete(null)
  }, [holidayToDelete, handleDeleteHoliday])

  const overrideInitialValues = useMemo(() => {
    if (!editingOverrideId) return null
    const row = hooks.overridesSource.find(
      (item) => item.clinic_override_id === editingOverrideId,
    )
    return row ? overrideApiToForm(row) : null
  }, [editingOverrideId, hooks.overridesSource])

  const holidayInitialValues = useMemo(() => {
    if (!editingHolidayId) return null
    const row = hooks.holidaysSource.find(
      (item) => item.clinic_holiday_id === editingHolidayId,
    )
    return row ? holidayApiToForm(row) : null
  }, [editingHolidayId, hooks.holidaysSource])

  useEffect(() => {
    setHeaderButtons(
      <>
        <Button
          className="settings-ds-button"
          variant="secondary"
          size="md"
          type="button"
          disabled={!hasChanges || disabled}
          onClick={handleReset}
        >
          Cancel
        </Button>
        <Button
          className="settings-ds-button"
          variant="primary"
          size="md"
          type="button"
          disabled={!hasChanges || disabled}
          onClick={() => {
            void handleSave()
          }}
        >
          Save changes
        </Button>
      </>,
    )
    return () => {
      setHeaderButtons(null)
    }
  }, [handleReset, handleSave, hasChanges, setHeaderButtons, disabled])

  const renderTypeBadge = useCallback(
    (label: string, color: BadgeColor): React.ReactNode => (
      <Badge mode={badgeMode} color={color} label={label} />
    ),
    [badgeMode],
  )

  const overrideColumns: DataTableColumn<DateOverrideRow>[] = useMemo(
    () => [
      { id: 'description', label: 'Description', sortable: true },
      { id: 'date', label: 'Date', sortable: true },
      { id: 'overrideHours', label: 'Override hours', sortable: true },
      {
        id: 'overrideType',
        label: 'Override type',
        sortable: true,
        renderCell: (row) =>
          row.overrideType === 'closed'
            ? renderTypeBadge('Practice closed', 'red')
            : renderTypeBadge('Adjusted hours', 'vivid-green'),
      },
    ],
    [renderTypeBadge],
  )

  const holidayColumns: DataTableColumn<HolidayRow>[] = useMemo(
    () => [
      { id: 'description', label: 'Description', sortable: true },
      { id: 'date', label: 'Date', sortable: true },
      { id: 'duration', label: 'Day-off duration', sortable: true },
      {
        id: 'holidayType',
        label: 'Holiday type',
        sortable: true,
        renderCell: (row) =>
          row.holidayType === 'private'
            ? renderTypeBadge('Private', 'blue')
            : renderTypeBadge('Public', 'vivid-green'),
      },
    ],
    [renderTypeBadge],
  )

  const sortedOverrides = useMemo(
    () => sortRows(hooks.dateOverrides, overrideSort, readSortValue),
    [hooks.dateOverrides, overrideSort],
  )
  const sortedHolidays = useMemo(
    () => sortRows(hooks.holidays, holidaySort, readSortValue),
    [hooks.holidays, holidaySort],
  )
  const overridePage = usePagedRows(sortedOverrides)
  const holidayPage = usePagedRows(sortedHolidays)

  const tableState = (rowCount: number): 'loading' | 'empty' | 'default' => {
    if (!hooks.dataReady) return 'loading'
    return rowCount === 0 ? 'empty' : 'default'
  }

  return (
    <div className="opening-hours-settings" id="opening-hours-settings">
      <section className="oh-section">
        <SectionHeader
          title="Working hours"
          info="Set the regular weekly hours of your clinic."
        />

        <div className="oh-first-day">
          <Select
            label="First day of week"
            placeholder="Select"
            options={DAY_OPTIONS}
            value={hooks.firstDayOfWeek}
            disabled={disabled}
            onChange={(value) =>
              hooks.setFirstDayOfWeek(
                String(value) as typeof hooks.firstDayOfWeek,
              )
            }
          />
          <Checkbox
            className="oh-same-hours"
            label="Same working hours"
            checked={hooks.sameWorkingHours}
            disabled={disabled}
            onChange={(e) => hooks.setSameWorkingHours(e.target.checked)}
          />
        </div>

        <div className="oh-days">
          {OPENING_HOURS_DAYS.map((day) => {
            const row = hooks.weekSchedule[day]
            const timeDisabled = disabled || !row.enabled
            return (
              <div key={day} className="oh-day-row">
                <div className="oh-day-toggle">
                  <ToggleSwitch
                    checked={row.enabled}
                    disabled={disabled}
                    aria-label={`${day} open`}
                    onChange={(checked) =>
                      hooks.updateDay(day, { enabled: checked })
                    }
                  />
                  <span className="oh-day-name">{day}</span>
                </div>
                <Select
                  className="oh-time-select"
                  label={`${day} start time`}
                  options={TIME_OPTIONS}
                  value={row.startTime}
                  disabled={timeDisabled}
                  onChange={(value) =>
                    hooks.updateDay(day, { startTime: String(value) })
                  }
                />
                <Select
                  className="oh-time-select"
                  label={`${day} end time`}
                  options={TIME_OPTIONS}
                  value={row.endTime}
                  disabled={timeDisabled}
                  onChange={(value) =>
                    hooks.updateDay(day, { endTime: String(value) })
                  }
                />
              </div>
            )
          })}
        </div>
      </section>

      <section className="oh-section">
        <SectionHeader
          title="Date override"
          info="Override your hours for specific dates."
          action={
            <Button
              className="settings-ds-button"
              variant="primary"
              size="md"
              type="button"
              disabled={disabled}
              onClick={openAddOverride}
            >
              Add overrides
            </Button>
          }
        />
        <DataTable<DateOverrideRow>
          className="oh-table"
          columns={overrideColumns}
          rows={overridePage.pageRows}
          state={tableState(hooks.dateOverrides.length)}
          emptyState={{
            title: 'No date override added',
            description: 'There’s no data to show here',
          }}
          sortColumnId={overrideSort.columnId}
          sortDirection={
            overrideSort.columnId ? overrideSort.direction : undefined
          }
          onSort={toggleOverrideSort}
          actionsLabel="Action/s"
          renderActions={(row) => (
            <SettingsRowActions
              disabled={disabled}
              onEdit={() => openEditOverride(row.id)}
              onDelete={() => setOverrideToDelete(row)}
            />
          )}
          pagination={overridePage.pagination}
        />
      </section>

      <section className="oh-section">
        <SectionHeader
          title="Holidays / Day-offs"
          info="Manage your clinic's holidays and day-offs."
          action={
            <Button
              className="settings-ds-button"
              variant="primary"
              size="md"
              type="button"
              disabled={disabled}
              onClick={openAddHoliday}
            >
              Add Holiday
            </Button>
          }
        />
        <DataTable<HolidayRow>
          className="oh-table"
          columns={holidayColumns}
          rows={holidayPage.pageRows}
          state={tableState(hooks.holidays.length)}
          emptyState={{
            title: 'No holidays / day-offs added',
            description: 'There’s no data to show here',
          }}
          sortColumnId={holidaySort.columnId}
          sortDirection={
            holidaySort.columnId ? holidaySort.direction : undefined
          }
          onSort={toggleHolidaySort}
          actionsLabel="Action/s"
          renderActions={(row) => (
            <SettingsRowActions
              disabled={disabled}
              onEdit={() => openEditHoliday(row.id)}
              onDelete={() => setHolidayToDelete(row)}
            />
          )}
          pagination={holidayPage.pagination}
        />
      </section>

      <AddOverride
        isOpen={addOverrideOpen}
        mode={editingOverrideId ? 'edit' : 'add'}
        initialValues={overrideInitialValues}
        onRequestClose={closeAddOverride}
        onSubmit={async (form) => {
          await hooks.handleSaveOverride(form, editingOverrideId)
        }}
        isSubmitting={hooks.savingOverride}
      />
      <AddHoliday
        isOpen={addHolidayOpen}
        mode={editingHolidayId ? 'edit' : 'add'}
        initialValues={holidayInitialValues}
        onRequestClose={closeAddHoliday}
        onSubmit={async (form) => {
          await hooks.handleSaveHoliday(form, editingHolidayId)
        }}
        isSubmitting={hooks.savingHoliday}
      />

      <DeleteConfirmationModal
        show={!!overrideToDelete}
        onHide={() => setOverrideToDelete(null)}
        onConfirm={confirmDeleteOverride}
        entityName={overrideToDelete?.description ?? ''}
      />
      <DeleteConfirmationModal
        show={!!holidayToDelete}
        onHide={() => setHolidayToDelete(null)}
        onConfirm={confirmDeleteHoliday}
        entityName={holidayToDelete?.description ?? ''}
      />
    </div>
  )
}

export default OpeningHours
